import * as debug from 'debug';
import { catOptions, index } from 'fp-ts/lib/Array';
import { fromEither } from 'fp-ts/lib/Option';
import * as io from 'io-ts';
import { getRootUrl } from 'sdi/app';
import { scopeOption } from 'sdi/lib';
import { Path, Router } from 'sdi/router';
import { uuidIO } from 'sdi/source';
import { tryNumber } from 'sdi/util';
import { getCurrentMap } from '../queries/app';
import { getView } from '../queries/map';
import {
    loadAllMaps,
    loadMap,
    setCurrentFeatureById,
    setCurrentMap,
    setLayout,
} from './app';
import { viewEvents } from './map';

const logger = debug('sdi:route');

/**
 * Keeping the old code for immediate reference for few more iterations — pm (2026-20-02)
 */
// const setMapView = () => {
//     const r = cleanRoute();
//     scopeOption()
//         .let('lat', fromNullable(getNumber(r[1])))
//         .let('lon', fromNullable(getNumber(r[2])))
//         .let('zoom', fromNullable(getNumber(r[3])))
//         .map(({ lat, lon, zoom }) => {
//             viewEvents.updateMapView({
//                 dirty: 'geo',
//                 center: [lat, lon],
//                 zoom,
//             });
//         });
// };
// export const navigate = () => {
//     const r = cleanRoute();
//     if (r.length > 0) {
//         const currentMapId = getCurrentMap();
//         const mapId = r[0];
//         if (mapId !== currentMapId) {
//             assign('app/current-map', mapId);
//             loadMap();
//         }

//         if (r[1] === 'feature') {
//             const layerId: string = r[2];
//             const featureId = fromNullable(r[3]).chain(tryNumber);
//             setLayout('map-and-feature');
//             featureId.map(fid => setCurrentFeatureById(layerId, fid));
//         } else {
//             prepareGeometryForHarvesting();
//             setMapView();
//             setLayout('map-and-info');
//         }
//     } else {
//         if (getMapList(getMaps()).length > 0) {
//             clearMap();
//             setLayout('map-navigator-fs');
//         }
//         loadAllMaps();
//     }
// };

const RouteIO = io.union([
    io.literal('index'),
    io.literal('map'),
    io.literal('feature'),
]);

export type Route = io.TypeOf<typeof RouteIO>;

export const { home, route, navigate } = Router<Route>('view');

const MapPointerIO = io.tuple(
    [io.number, io.number, io.Integer],
    'MapPointerIO'
);

const mapPointerParser = (path: Path) =>
    fromEither(MapPointerIO.decode(catOptions(path.map(tryNumber))));

const mapPathParser = (path: Path) =>
    index(0, path).chain(id =>
        fromEither(uuidIO.decode(id)).map(id => ({
            id,
            pointer: mapPointerParser(path.slice(1)),
        }))
    );

const featurePathParser = (path: Path) =>
    scopeOption()
        .let(
            'mapId',
            index(0, path).chain(id => fromEither(uuidIO.decode(id)))
        )
        .let('layerId', index(1, path))
        .let(
            'featureId',
            index(2, path).map(idString =>
                tryNumber(idString)
                    .map<number | string>(id => id)
                    .getOrElse(idString)
            )
        );

home('index', () => {
    loadAllMaps();
    setLayout('map-navigator-fs');
});

route(
    'map',
    opt =>
        opt.map(({ id, pointer }) => {
            if (id !== getCurrentMap()) {
                setCurrentMap(id);
            }
            loadMap();
            setLayout('map-and-info');

            pointer.map(([lat, lon, zoom]) => {
                viewEvents.updateMapView({
                    dirty: 'geo',
                    center: [lat, lon],
                    zoom,
                });
            });
        }),
    mapPathParser
);

route(
    'feature',
    opt =>
        opt.map(({ mapId, layerId, featureId }) => {
            if (mapId !== getCurrentMap()) {
                setCurrentMap(mapId);
            }
            loadMap();
            setCurrentFeatureById(layerId, featureId);
            setLayout('map-and-feature');
        }),
    featurePathParser
);

export const navigateHome = () => {
    navigate('index', []);
};

export const navigateMap = (mid: string) => navigate('map', [mid]);

export const navigateMapFeature = (
    mid: string,
    lid: string,
    fid: string | number
) => navigate('feature', [mid, lid, fid]);

// Ugly, but I don't have time to rewrite the whole router now - pm
// And now it's not even obvious what it was for — pm
export const navigateMapInPlace = (mid: string) => navigate('map', [mid]);

export const loadInitialRoute = (initial: string[]) =>
    index(0, initial)
        .chain(prefix => fromEither(RouteIO.decode(prefix)))
        .map(prefix => navigate(prefix, initial.slice(1)))
        .getOrElseL(navigateHome);

export const getMapUrlFromView = (mapId: string) => {
    const round = Math.round;
    const view = getView();
    if (view.center.length == 2) {
        const lat = round(view.center[0]);
        const lon = round(view.center[1]);
        const zoom = round(view.zoom);
        return `${getRootUrl('view')}/map/${mapId}/${lat}/${lon}/${zoom}`;
    }
    return `${getRootUrl('view')}/${mapId}/`;
};

logger('loaded');
